/**
 * Local row + helper types for the Consumption tab's SKU-velocity table.
 *
 * `ConsumptionRow` mirrors the shape returned by `useSkuVelocity` (see
 * `useReportingV2.ts`) so the table's sort/filter code is fully typed without
 * leaning on the inferred tRPC output. Pure types — no React, no runtime.
 */

export type ConsumptionTrend = 'rising' | 'falling' | 'flat';
export type ConsumptionStockKind = 'all' | 'preprinted' | 'other';

export interface ConsumptionRow {
  skuId: string;
  sku: string;
  type: string | null;
  color: string | null;
  size: string | null;
  category: string;
  /** True when the fulfilled unit consumed finished pre-printed stock. */
  isPreprinted: boolean;
  /** Net fulfilled inventory units in the window, after returns/corrections. */
  units: number;
  /** Gross stock decrements before returns/corrections. */
  grossUnits: number;
  /** Positive fulfilment corrections and restocks in the window. */
  returnedUnits: number;
  /** Net units from the immediately preceding equal-length window. */
  previousUnits: number;
  /** Distinct fulfilled Shopify orders with a net decrement for this SKU. */
  orders: number;
  /** Units / window_days — period-based, comparable across SKUs. */
  velocity: number;
  /** Fractional change vs prior (0.25 = +25%); null when there is no prior. */
  deltaPct: number | null;
  trend: ConsumptionTrend;
  /** Pooled on-hand — may be NEGATIVE (oversold); render the real value. */
  onHand: number;
  /** Clamped >= 0; null = no velocity. */
  daysOfCover: number | null;
  firstConsumedAt: string | null;
  lastConsumedAt: string | null;
}

export interface PreprintedConsumptionSummary {
  units: number;
  skuCount: number;
  share: number;
}

/** Match a report row against the user-facing stock-kind filter. */
export function matchesConsumptionStockKind(
  row: ConsumptionRow,
  stockKind: ConsumptionStockKind,
): boolean {
  if (stockKind === 'preprinted') return row.isPreprinted;
  if (stockKind === 'other') return !row.isPreprinted;
  return true;
}

/** Summarise net pre-printed usage without allowing a zero-total NaN share. */
export function summarizePreprintedConsumption(
  rows: ConsumptionRow[],
): PreprintedConsumptionSummary {
  let totalUnits = 0;
  let units = 0;
  let skuCount = 0;

  for (const row of rows) {
    totalUnits += row.units;
    if (!row.isPreprinted) continue;
    units += row.units;
    skuCount += 1;
  }

  return {
    units,
    skuCount,
    share: totalUnits > 0 ? units / totalUnits : 0,
  };
}

/** Pareto ABC class derived from cumulative unit share. */
export type AbcClass = 'A' | 'B' | 'C';

/**
 * Classify rows into ABC bands by cumulative share of total units (Pareto):
 * A ≤ 80%, B ≤ 95%, C the rest. Returns a map keyed by SKU so the table can
 * look the class up per row regardless of the active sort order.
 *
 * The classification is computed over the FULL row set (not the visible/filtered
 * subset) so a SKU's ABC class is a stable property of the period, not an
 * artefact of the current search box. Rows are ranked by units desc.
 */
export function classifyAbc(rows: ConsumptionRow[]): Map<string, AbcClass> {
  const result = new Map<string, AbcClass>();
  const total = rows.reduce((sum, r) => sum + Math.max(0, r.units), 0);
  if (total <= 0) {
    for (const r of rows) result.set(r.sku, 'C');
    return result;
  }
  const ranked = [...rows].sort((a, b) => b.units - a.units);
  let cumulative = 0;
  for (const r of ranked) {
    cumulative += Math.max(0, r.units);
    const share = cumulative / total;
    result.set(r.sku, share <= 0.8 ? 'A' : share <= 0.95 ? 'B' : 'C');
  }
  return result;
}

/** Tailwind badge classes per ABC class — EditingPill ring geometry (DESIGN.md §7). */
export const ABC_BADGE: Record<AbcClass, string> = {
  A: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300/80',
  B: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300/80',
  C: 'bg-muted text-muted-foreground ring-1 ring-border',
};
